import React, { useState } from 'react';
import {
  AppBar, Box, Toolbar, IconButton, Button, Avatar, Drawer,
  List, ListItem, ListItemIcon, ListItemText, Divider,
  useMediaQuery, useTheme, Container, Stack, Chip,
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard, Person, Logout, Home,
  Assignment, Rocket, Groups, Close, Login, AppRegistration,
  WorkspacePremium, Upgrade,
} from '@mui/icons-material';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { logout } from '../../store/slices/authSlice';

// COLOR SETTINGS: Matches the exact deep forest green from your 3D logo
const colors = {
  primary: {
    darkest: '#0D3D12', // The core dark green from the logo
    main: '#1B4D1F',
    medium: '#2E7D32',
    // Seamless gradient to blend the rest of the bar with the logo image
    gradient: 'linear-gradient(90deg, #0D3D12 0%, #144618 50%, #1B4D1F 100%)',
  },
};

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const hasPremium = user?.is_premium || user?.has_premium_access;

  const handleLogout = async () => {
    setMobileDrawerOpen(false);
    await dispatch(logout());
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  const publicLinks = [
    { text: 'Home', icon: <Home />, path: '/' },
    { text: 'Problems', icon: <Assignment />, path: '/problems' },
    { text: 'Solutions', icon: <Rocket />, path: '/solutions' },
    { text: 'Organizations', icon: <Groups />, path: '/organizations' },
  ];

  const dashboardLink = { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' };
  const allLinks = isAuthenticated ? [...publicLinks, dashboardLink] : publicLinks;

  const activeBtnSx = (path) => ({
    color: 'white',
    textTransform: 'none',
    fontWeight: isActive(path) ? 700 : 500,
    px: 2,
    borderRadius: '24px',
    bgcolor: isActive(path) ? 'rgba(255,255,255,0.15)' : 'transparent',
    borderBottom: isActive(path) ? '2px solid rgba(255,255,255,0.8)' : '2px solid transparent',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
  });

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          // Set to the exact dark green base of the logo
          background: colors.primary.darkest, 
          backgroundImage: colors.primary.gradient,
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ height: { xs: 70, md: 95 } }}>

            {isMobile && (
              <IconButton onClick={() => setMobileDrawerOpen(true)} sx={{ mr: 1, color: 'white' }}>
                <MenuIcon />
              </IconButton>
            )}

            {/* LOGO BOX - Perfectly blended */}
            <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', mr: 4, height: '100%' }}>
              <Box
                component="img"
                src="/assets/images/rengigs-logo.jpg"
                alt="Rengigs"
                sx={{ 
                  height: { xs: 60, md: 85 }, 
                  width: 'auto',
                  borderRadius: '4px',
                  // No drop shadow here helps it blend into the background as one piece
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.02)' }
                }}
              />
            </Box>

            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {allLinks.map((item) => (
                  <Button
                    key={item.text}
                    component={Link}
                    to={item.path}
                    startIcon={item.icon}
                    sx={activeBtnSx(item.path)}
                  >
                    {item.text}
                  </Button>
                ))}
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto' }}>
              {isAuthenticated ? (
                <>
                  {!isMobile && hasPremium && (
                    <Chip
                      icon={<WorkspacePremium sx={{ fontSize: 15, color: '#CDDC39 !important' }} />}
                      label="Premium"
                      size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
                    />
                  )}
                  <IconButton component={Link} to="/dashboard" sx={{ p: 0.5, border: '2px solid rgba(255,255,255,0.3)' }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)' }}>
                      {user?.first_name?.[0] || <Person />}
                    </Avatar>
                  </IconButton>
                  <IconButton onClick={handleLogout} sx={{ color: 'white' }}><Logout /></IconButton>
                </>
              ) : (
                !isMobile && (
                  <Stack direction="row" spacing={1.5}>
                    <Button component={Link} to="/login" variant="outlined" sx={{ color: 'white', borderColor: 'white', borderRadius: '24px', textTransform: 'none' }}>
                      Sign In
                    </Button>
                    <Button component={Link} to="/register" variant="contained" sx={{ bgcolor: 'white', color: colors.primary.darkest, borderRadius: '24px', fontWeight: 700, textTransform: 'none' }}>
                      Join Now
                    </Button>
                  </Stack>
                )
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer with matching background */}
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        PaperProps={{ sx: { width: 280, background: colors.primary.darkest, color: 'white' } }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <img src="/assets/images/rengigs-logo.jpg" alt="Logo" style={{ height: '40px' }} />
          <IconButton onClick={() => setMobileDrawerOpen(false)} sx={{ color: 'white' }}><Close /></IconButton>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <List>
          {allLinks.map((item) => (
            <ListItem button key={item.text} component={Link} to={item.path} onClick={() => setMobileDrawerOpen(false)}>
              <ListItemIcon sx={{ color: 'white' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
        </List>
      </Drawer>
    </>
  );
};

export default Header;